import { useState } from 'react';

import { addToCart } from 'helpers/google-ecommerce/events/addToCart';
import { detail } from 'helpers/google-ecommerce/events/detail';
import { impressions } from 'helpers/google-ecommerce/events/impressions';
import { productClick } from 'helpers/google-ecommerce/events/productClick';
import { removeFromCart } from 'helpers/google-ecommerce/events/removeFromCart';
import { isMobileSource } from 'pages/leads/constants';
import { useSearchParam } from 'react-use';

import { promoView } from '../events/promoView';
import { IPromotion } from '../types';

interface IDimension {
  [key: string]: string;
}

const useGoogleEcommerce = (section: string, page: string, initialPrice: string) => {
  const sourceParam = useSearchParam('source');
  const isMobile = isMobileSource(sourceParam);
  const [activePrice, setPrice] = useState(initialPrice);

  const getProducts = (othersProperties?: any) => {
    const INITIAL_PRODUCT = {
      name: page,
      id: page,
      category: section,
      list: isMobile ? 'app' : 'web',
      price: activePrice,
    };

    return {
      ...INITIAL_PRODUCT,
      ...othersProperties,
    };
  };

  const getDimensionsOptions = (formDimensions: string[] | undefined): IDimension => {
    const dimensions: { [key: string]: string } = {};
    formDimensions?.forEach((dimension, index) => {
      dimensions[`dimension${index + 1}`] = dimension;
    });

    return dimensions;
  };

  const setImpression = () => {
    impressions([getProducts()]);
  };

  const addProductToCart = (formValues: string[]) => {
    const dimensions = getDimensionsOptions(formValues);
    const products = getProducts(dimensions);
    const eventSetup = {
      products: [products],
    };

    addToCart(eventSetup);
  };

  const applyProductToCart = (otherOptions?: {}) => {
    const products = getProducts(otherOptions);
    const eventSetup = {
      products: [products],
    };

    addToCart(eventSetup);
  };

  const removeProductFromCart = (formValues: string[]) => {
    const dimensions = getDimensionsOptions(formValues);
    const products = getProducts(dimensions);
    const eventSetup = {
      products: [products],
    };

    removeFromCart(eventSetup);
  };

  const setProductDetails = (formValues: string[]) => {
    const dimensions = getDimensionsOptions(formValues);
    const products = getProducts(dimensions);
    const eventSetup = {
      products: [products],
      actionField: { list: page },
    };

    detail(eventSetup);
  };

  const applyProductDetails = (otherOptions?: {}) => {
    const products = getProducts(otherOptions);
    const eventSetup = {
      products: [products],
      actionField: { list: page },
    };

    detail(eventSetup);
  };

  const applyPromoView = (promotions: IPromotion[]) => {
    const eventSetup = {
      promotions,
    };
    promoView(eventSetup);
  };

  const setProductClick = (formValues: string[]) => {
    const dimensions = getDimensionsOptions(formValues);
    const products = getProducts(dimensions);
    const eventSetup = {
      products: [products],
    };

    productClick(eventSetup);
  };

  return {
    addProductToCart,
    removeProductFromCart,
    setImpression,
    setPrice,
    setProductClick,
    setProductDetails,
    applyProductToCart,
    applyProductDetails,
    applyPromoView,
  };
};

export default useGoogleEcommerce;
